import "./ExpenseRefundFolder.css";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, checkRoles, exportFile, showError, showSuccess, trimData } from "../../../utils/utilities";
import { setFilters } from "../../../redux/ducks/Filters";
import {
  dbDate,
  expenseRefundViewing,
  fileFormatXextension,
  langDate,
  paginationDefaults,
} from "../../../utils/properties";
import { cloneDeep, get, isEmpty, toLower, truncate } from "lodash";
import { useTranslation } from "react-i18next";
import Filter from "../../../components/functional_components/Filter/Filter";
import { Card, Col, DatePicker, Input, Modal, Pagination, Row, Select, Spin, Switch, Tooltip } from "antd";
import {
  FolderOpenOutlined,
  PlusOutlined,
  SaveOutlined,
  EditOutlined,
  SelectOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import { useNavigate } from "react-router-dom";
import ExpenseRefundFolder from "../../../services/api/ExpenseRefundFolder";
import { setLoading } from "../../../redux/ducks/Loading";
import CostCentre from "../../../services/api/CostCentre";
import ProjectTask from "../../../services/api/ProjectTask";
import User from "../../../services/api/User";
import ExpenseRefund from "../../../services/api/ExpenseRefund";
import dayjs from "dayjs";

const ExpenseRefundFolders = ({ showAll = false }) => {
  const { confirm } = Modal;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const currentLanguage = useSelector((state) =>
    state.languageDuck?.currentLanguage === null ? "GB" : get(state.languageDuck, "currentLanguage", "GB")
  );
  const currentMonth = dayjs().format("YYYYMM");
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const dateFormat = langDate[toLower(currentLanguage)];
  const [state, setState] = useState({
    lastMonthClosed: currentMonth,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    tableContent: [],
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    cdcList: [],
    prjList: [],
    hrSearchList: [],
    substitutedSearchList: [],
    canEdit: false,
    downloadLoading: false,
  });

  const onSearchusers = async (value) => {
    if (value.length >= 2) {
      const { error, data } =
        checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]) &&
        !checkRoles(userInfo.roles, expenseRefundViewing["expenseRefundFolderUsers"].view)
          ? await User.getValidSubstituteUsersList(`allPages=true&surname=${value}`)
          : await User.getUsersInternalHrList(`allPages=true&surname=${value}`);
      if (!error && data) {
        setState((prevState) => ({
          ...prevState,
          hrSearchList: data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })),
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, hrSearchList: [] }));
    }
  };

  const onSearchuToSubstituted = async (value) => {
    const { error, data } = await User.getValidSubstituteUsersList(`allPages=true&surname=${value}`);
    if (!error && data) {
      setState((prevState) => ({
        ...prevState,
        substitutedSearchList: data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })),
      }));
    }
  };

  useEffect(() => {
    // reset page to 0 but keep the user, when present (the page must be reset because filters.obj.page is used for both the expense folders pagination and the single folder's expense notes results pagination: if I go to page 2 of expense notes in a certain folder, when I go back to the folders results I don't want to go to page 2 of the folders results)
    filters.obj.page = paginationDefaults.page - 1;
    const queryString = buildQueryString(filters.obj, !showAll ? { "user.username": userInfo.username } : {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
    formikFilter.resetForm();
  }, [showAll]);

  const formikExpenseReport = useFormik({
    initialValues: {
      substituted: null,
      switcher: state.modal.data?.projectTask?.id ? true : false,
      costCentre: get(state.modal.data, "costCentre.id", null),
      projectTask: get(state.modal.data, "projectTask.id", null),
      periodStart:
        get(state.modal.data, "periodStart", null) !== null ? dayjs(state.modal.data.periodStart, dbDate) : null,
      periodEnd: get(state.modal.data, "periodEnd", null) !== null ? dayjs(state.modal.data.periodEnd, dbDate) : null,
      note: get(state.modal.data, "note", ""),
      flatRate: get(state.modal.data, "flatRate", false),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (
        !values.substituted &&
        checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]) &&
        showAll &&
        state.modal.type === "new"
      ) {
        errors.substituted = "Required!";
      }
      if (!values.switcher && !values.costCentre) {
        errors.costCentre = "Required!";
      }
      if (values.switcher && !values.projectTask) {
        errors.projectTask = "Required!";
      }
      if (!values.periodStart) {
        errors.periodStart = "Required!";
      }
      if (!values.periodEnd) {
        errors.periodEnd = "Required!";
      }
      if (!values.note) {
        errors.note = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        id: state.modal.data?.id || undefined,
        periodStart: dayjs(values.periodStart, dateFormat).format(dbDate),
        periodEnd: dayjs(values.periodEnd, dateFormat).format(dbDate),
        costCentre: !values.switcher ? state.cdcList.find((c) => c.id === values.costCentre) : null,
        projectTask: values.switcher ? state.prjList.find((p) => p.id === values.projectTask) : null,
        user: { username: values.substituted },
      };
      const saveResponse = await ExpenseRefundFolder.setExpenseRefundFolder(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, !showAll ? { "user.username": userInfo.username } : {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const closeModal = () => {
    formikExpenseReport.resetForm();
    setState((prevState) => ({
      ...prevState,
      substitutedSearchList: [],
      modal: { visible: false, type: "", data: {} },
    }));
  };

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } =
      await ExpenseRefundFolder.getExpenseRefundFolders(queryString);
    if (!error && data) {
      let canEdit = true;
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      if (showAll) {
        canEdit =
          checkRoles(userInfo.roles, expenseRefundViewing["expenseRefundFolderUsers"].edit) ||
          checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]);
      }
      setState((prevState) => ({
        ...prevState,
        tableContent: data,
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
        canEdit,
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };
  const formikFilter = useFormik({
    initialValues: {
      periodStart: filters.obj.periodStart ? dayjs(filters.obj.periodStart, dbDate) : null,
      periodEnd: filters.obj.periodEnd ? dayjs(filters.obj.periodEnd, dbDate) : null,
      user: filters.obj["user.username"] ? filters.obj["user.username"] : null,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = "0";
      delete dataFilter["user"];
      if (!showAll) {
        dataFilter["user.username"] = userInfo.username;
      } else {
        if (values.user) {
          dataFilter["user.username"] = values.user;
          delete dataFilter["user"];
        } else {
          delete dataFilter["user.username"];
        }
      }
      if (values.periodStart) {
        dataFilter.periodStart =
          dataFilter.periodStart !== "" ? dayjs(dataFilter.periodStart, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodStart;
        delete filters.obj.periodStart;
      }
      if (values.periodEnd) {
        dataFilter.periodEnd =
          dataFilter.periodEnd !== "" ? dayjs(dataFilter.periodEnd, dateFormat).format(dbDate) : undefined;
      } else {
        delete dataFilter.periodEnd;
        delete filters.obj.periodEnd;
      }
      const queryString = buildQueryString(filters.obj, dataFilter);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const resetFilter = () => {
    const queryString = buildQueryString(
      { page: paginationDefaults.page - 1, size: paginationDefaults.size },
      !showAll ? { "user.username": userInfo.username } : {}
    );
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const fieldsFilter = [
    {
      type: "date",
      allowClear: true,
      isRequired: false,
      id: "periodStart",
      name: "periodStart",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodStart"),
      value: formikFilter.values.periodStart,
      onChange: (date) => {
        formikFilter.setFieldValue("periodStart", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "date",
      isRequired: false,
      id: "periodEnd",
      name: "periodEnd",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodEnd"),
      value: formikFilter.values.periodEnd,
      onChange: (date) => {
        formikFilter.setFieldValue("periodEnd", date);
      },
      onBlur: formikFilter.handleBlur,
    },
  ];
  const fieldsFilterUser = [
    {
      type: "date",
      isRequired: false,
      id: "periodStart",
      name: "periodStart",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodStart"),
      value: formikFilter.values.periodStart,
      allowClear: true,
      onChange: (date) => {
        formikFilter.setFieldValue("periodStart", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "date",
      isRequired: false,
      id: "periodEnd",
      name: "periodEnd",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("fuelCard.table.periodEnd"),
      value: formikFilter.values.periodEnd,
      allowClear: true,
      onChange: (date) => {
        formikFilter.setFieldValue("periodEnd", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "selectWithSearch",
      allowClear: false,
      isRequired: false,
      id: "user",
      name: "user",
      value: formikFilter.values.user,
      label: t("fuelCard.table.user") + " " + t("fuelCard.userLabelInfo"),
      onChange: formikFilter.handleChange("user"),
      onBlur: formikFilter.handleBlur("user"),
      onSearch: (e) => onSearchusers(e),
      dataSelect: state.hrSearchList,
    },
  ];

  const onSearchCdc = async (value) => {
    if (value.length >= 2) {
      const { error, data } = await CostCentre.costCentre(`allPages=true&id=${value}&management=false`);
      if (!error && data) {
        setState((prevState) => ({
          ...prevState,
          cdcList: data,
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, cdcList: [] }));
    }
  };

  const onSearchPrj = async (value) => {
    if (value.length >= 2) {
      const { error, data } = await ProjectTask.getProjectTasksAndPhasesOfProject(`allPages=true&id=${value}`);
      if (!error && data) {
        setState((prevState) => ({
          ...prevState,
          prjList: data,
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, prjList: [] }));
    }
  };

  const editCallback = (item) => async () => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await ExpenseRefundFolder.getExpenseRefundFolder(item.id);
    if (!error && data) {
      data.isEmpty = item.isEmpty;
      data.minPeriodStart = item.minPeriodStart;
      data.maxPeriodEnd = item.maxPeriodEnd;
      setState((prevState) => ({
        ...prevState,
        cdcList: !isEmpty(data.costCentre) ? [data.costCentre] : [],
        prjList: !isEmpty(data.projectTask) ? [data.projectTask] : [],
        modal: { visible: true, type: "edit", data },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => async () => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, loading: true }));
        const deleteResponse = await ExpenseRefundFolder.deleteExpenseRefundFolder(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, !showAll ? { "user.username": userInfo.username } : {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, loading: false }));
      },
      onCancel() {},
    });
  };

  const handleDownload = () => async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const file = await ExpenseRefund.expenseRefundReport();
    if (!file.error) {
      const downloadType = fileFormatXextension["xlsx"];
      const blob = new Blob([file], { type: downloadType });
      exportFile(blob, "report.xlsx");
      showSuccess();
    } else showError(get(file, "error.messageCode", "ER000"));

    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  const isFlatRateSwitchDisabled = () => {
    if (state.modal.data) {
      if (state.modal.data.isEmpty) {
        return formikExpenseReport.values.substituted ? false : !state.canEdit;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  return (
    <>
      <Filter
        formikForm={formikFilter}
        fields={!showAll ? fieldsFilter : fieldsFilterUser}
        onReset={resetFilter}
        allInOneRow={showAll}
      />
      <Row type="flex" align="middle" justify={showAll ? "end" : "space-between"} className="mb-2em">
        {!showAll && (
          <Button
            className={"mr-10"}
            isLoading={state.downloadLoading}
            currentIcon={<DownloadOutlined className="icon-medium" />}
            clickCallback={handleDownload()}
          />
        )}
        <Button
          className="mr-10"
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
          isDisabled={checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]) ? false : showAll}
        />
      </Row>
      <Row gutter={[16, 16]}>
        {state.tableContent.map((f) => (
          <Col key={f.id} span={6} className="gutter-row folder-container p-relative">
            <div style={{ position: "relative" }}>
              <Card
                style={{ position: "relative" }}
                className="folder-card"
                actions={[
                  /*  (`${item.id}`, { state: { supplierName: item.name } })*/
                  <SelectOutlined
                    key="enter"
                    onClick={() => {
                      navigate(!showAll ? `/expense-refund-folder/${f.id}` : `/expense-refund-folder-users/${f.id}`, {
                        state: { canEdit: f.canEdit },
                      });
                    }}
                  />,
                  <div
                    className={`list-actions ${
                      (!state.canEdit || !f.canDelete || (!f.canEdit && showAll)) && "disabled notAllowedCursor"
                    }`}
                    key="edit"
                    onClick={!state.canEdit || !f.canDelete || (!f.canEdit && showAll) ? undefined : editCallback(f)}
                  >
                    <EditOutlined className="icon-small" />
                  </div>,
                  <div
                    className={`list-actions ${
                      (!state.canEdit || !f.canDelete || (!f.canEdit && showAll)) && "disabled notAllowedCursor"
                    }`}
                    key="delete"
                    onClick={
                      !state.canEdit || !f.canDelete || (!f.canEdit && showAll) ? undefined : deleteCallback(f.id)
                    }
                  >
                    <DeleteOutlined className="icon-small" />
                  </div>,
                ]}
              >
                {(f.isRefutedPresent || f.isPlanningPresent) && (
                  <div style={{ position: "absolute", marginTop: "-10px" }}>
                    {f.isRefutedPresent && <WarningOutlined className="PFred icon-large noCursorPointer mt-10 ml-10" />}
                    {f.isPlanningPresent && (
                      <WarningOutlined className="PForange icon-large noCursorPointer mt-10 ml-10" />
                    )}
                  </div>
                )}
                <FolderOpenOutlined className="folder-icon" />

                <p style={{ margin: 0 }}>
                  <Tooltip title={f.note}>
                    {truncate(f.note, {
                      length: 24,
                      separator: " ",
                    })}
                  </Tooltip>
                </p>
                <span style={{ fontSize: "10px" }}>
                  {dayjs(f.periodStart, dbDate).format(dateFormat)} - {dayjs(f.periodEnd, dbDate).format(dateFormat)}
                </span>
                {showAll && (
                  <p style={{ fontSize: "10px" }}>
                    {f.user?.name} {f.user?.surname}
                  </p>
                )}
              </Card>
            </div>
          </Col>
        ))}
      </Row>
      <Row justify="end">
        <Pagination
          current={state.pagination.currentPage}
          pageSize={state.pagination.size}
          total={state.pagination.totalItems}
          onChange={(page, pageSize) => {
            let newFilter = { page: page - 1, size: pageSize };
            if (!showAll) {
              newFilter = { ...newFilter, "user.username": userInfo.username };
            }
            const queryString = buildQueryString(filters.obj, newFilter);
            dispatch(setFilters(queryString));
            dataList(queryString.string);
          }}
          size="small"
        />
      </Row>
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.note
            ? state.modal.data.note
            : t("expenseRefundFolder.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikExpenseReport.submitForm}
            isDisabled={
              !(formikExpenseReport.dirty && formikExpenseReport.isValid) ||
              (formikExpenseReport.values.substituted ? false : !state.canEdit)
            }
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        {checkRoles(userInfo.roles, ["EXPENSE_REFUND_SUBST"]) && showAll && state.modal.type === "new" && (
          <Row align="middle" justify="center">
            <Select
              className="w-100"
              id="substituted"
              name="substituted"
              showSearch
              placeholder={t("fuelCard.table.user") + " " + t("fuelCard.userLabelInfo")}
              onChange={formikExpenseReport.handleChange("substituted")}
              onBlur={formikExpenseReport.handleBlur("substituted")}
              onSearch={(e) => onSearchuToSubstituted(e)}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              value={formikExpenseReport.values.substituted}
              options={
                state.substitutedSearchList.length > 0 &&
                state.substitutedSearchList.map((data) => ({ value: data.value, label: data.label, name: data.label }))
              }
            />
          </Row>
        )}
        <Row gutter={[40, 24]} align="middle" justify="center">
          <Col span={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="brand">
                {t("expenseRefundFolder.costCentre")}
              </label>
            </Row>
            <Select
              disabled={
                formikExpenseReport.values.substituted
                  ? formikExpenseReport.values.switcher
                  : formikExpenseReport.values.switcher || !state.canEdit
              }
              allowClear
              onClear={() => {
                setState((prevState) => ({ ...prevState, cdcList: [] }));
              }}
              className="w-100"
              id="costCentre"
              optionFilterProp="value"
              name="costCentre"
              showSearch
              placeholder={t("expenseRefundFolder.costCentre")}
              onChange={(value) => {
                formikExpenseReport.setFieldValue("costCentre", value);
              }}
              onBlur={() => {
                formikExpenseReport.handleBlur("costCentre");
              }}
              onSearch={(value) => onSearchCdc(value)}
              filterOption={(input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              value={formikExpenseReport.values.costCentre}
              options={
                state.cdcList.length > 0 &&
                state.cdcList.map((cdc) => ({ value: cdc.id, name: cdc.name, label: `${cdc.id} | ${cdc.name}` }))
              }
            />
          </Col>
          <Col span={4} style={{ textAlign: "center" }}>
            <Switch
              disabled={formikExpenseReport.values.substituted ? false : !state.canEdit}
              id="switcher"
              name="switcher"
              style={{ backgroundColor: "#1890FF" }}
              onChange={(e) => {
                formikExpenseReport.setFieldValue("costCentre", "");
                formikExpenseReport.setFieldValue("projectTask", "");
                formikExpenseReport.setFieldValue("switcher", e);
              }}
              onBlur={formikExpenseReport.handleBlur}
              checked={formikExpenseReport.values.switcher}
              checkedChildren={t("expenseRefundFolder.shortProject")}
              unCheckedChildren={t("expenseRefundFolder.shortCostCentre")}
            />
          </Col>
          <Col span={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="projectTask">
                {t("expenseRefundFolder.projectTask")}
              </label>
            </Row>
            <Select
              disabled={
                formikExpenseReport.values.substituted
                  ? !formikExpenseReport.values.switcher
                  : !formikExpenseReport.values.switcher || !state.canEdit
              }
              allowClear
              onClear={() => {
                setState((prevState) => ({ ...prevState, prjList: [] }));
              }}
              className="w-100"
              id="projectTask"
              optionFilterProp="value"
              name="projectTask"
              showSearch
              placeholder={t("expenseRefundFolder.projectTask")}
              onChange={(value) => {
                formikExpenseReport.setFieldValue("projectTask", value);
              }}
              onBlur={() => {
                formikExpenseReport.handleBlur("projectTask");
              }}
              onSearch={(value) => onSearchPrj(value)}
              filterOption={(input, option) => option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              value={formikExpenseReport.values.projectTask}
              options={
                state.prjList.length > 0 &&
                state.prjList.map((prj) => ({ value: prj.id, name: prj.name, label: `${prj.id} | ${prj.name}` }))
              }
            />
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-between">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="note">
                {t("expenseRefundFolder.table.note")}*
              </label>
            </Row>
            <Row>
              <Input
                disabled={formikExpenseReport.values.substituted ? false : !state.canEdit}
                className="w-100"
                required
                id="note"
                name="note"
                placeholder={t("expenseRefundFolder.table.note")}
                onChange={formikExpenseReport.handleChange}
                onBlur={formikExpenseReport.handleBlur}
                value={formikExpenseReport.values.note}
                showCount
                maxLength={40}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="note">
                {t("expenseRefundFolder.flatRate")}
              </label>
            </Row>
            <Switch
              id="flatRate"
              name="flatRate"
              onChange={(e) => {
                formikExpenseReport.setFieldValue("periodStart", null);
                formikExpenseReport.setFieldValue("periodEnd", null);
                formikExpenseReport.setFieldValue("flatRate", e);
              }}
              onBlur={formikExpenseReport.handleBlur}
              checked={formikExpenseReport.values.flatRate}
              disabled={isFlatRateSwitchDisabled()}
            />
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-between">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodStart">
                {t("expenseRefundFolder.periodStart")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                disabled={formikExpenseReport.values.substituted ? false : !state.canEdit}
                className="w-100"
                allowClear={false}
                id="periodStart"
                name="periodStart"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikExpenseReport.setFieldValue("periodStart", date);
                }}
                // onBlur={formikExpenseReport.handleBlur}
                onBlur={() => formikExpenseReport.handleBlur({ target: { name: "periodStart" } })}
                value={formikExpenseReport.values.periodStart}
                disabledDate={(current) => {
                  return current &&
                    ((state.modal.type === "edit" && state.modal.data?.isPlanningPresent) ||
                      state.modal.data?.isRefutedPresent)
                    ? current >= dayjs(state.modal.data?.minPeriodStart)
                    : current < dayjs("2024-01-01", "YYYY-MM-DD");
                }} //Request by pfp
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="periodEnd">
                {t("expenseRefundFolder.periodEnd")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                disabled={formikExpenseReport.values.substituted ? false : !state.canEdit}
                className="w-100"
                allowClear={false}
                id="periodEnd"
                name="periodEnd"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                onChange={(date) => {
                  formikExpenseReport.setFieldValue("periodEnd", date);
                }}
                onBlur={() => formikExpenseReport.handleBlur({ target: { name: "periodEnd" } })}
                value={formikExpenseReport.values.periodEnd}
                disabledDate={(current) => {
                  let minDate = dayjs(formikExpenseReport.values.periodStart);
                  if (
                    (state.modal.type === "edit" && state.modal.data?.isPlanningPresent) ||
                    state.modal.data?.isRefutedPresent
                  ) {
                    // min 1 day period (startDate <= endDate)
                    minDate = dayjs(state.modal.data?.maxPeriodEnd).subtract(1, "days");
                  } else if (formikExpenseReport.values.flatRate) {
                    // if it's a flat rate, min 2 days period (startDate < endDate)
                    minDate = dayjs(formikExpenseReport.values.periodStart).add(1, "days");
                  }
                  return current && current < minDate;
                }}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ExpenseRefundFolders;
